import { useEffect, useRef, useState, useCallback } from 'react';
import camelcaseKeys from 'camelcase-keys';
import type { DbPrompt, DbExecution } from '@qwickforge/api/db';

/**
 * Project type (camelCase matching TypeScript conventions)
 */
export interface DbProject {
  id: string;
  userId: string | null;
  orgId: string;
  name: string;
  currentDir: string | null;
  metadata: any;
  createdAt: Date;
  updatedAt: Date;
}

export interface DatabaseEvent {
  schema: string;
  table: string;
  commit_timestamp: string;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: any;
  old: any;
  errors: any;
}

export interface RealtimeEvent {
  eventType: string;
  payload: DatabaseEvent;
}

export interface RealtimeEventHandlers {
  onPromptInsert?: (payload: DbPrompt) => void;
  onPromptUpdate?: (payload: DbPrompt) => void;
  onExecutionInsert?: (payload: DbExecution) => void;
  onExecutionUpdate?: (payload: DbExecution) => void;
  onProjectUpdate?: (payload: DbProject) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Transform snake_case database payload to camelCase and parse dates
 *
 * This ensures:
 * 1. Type safety - fields match TypeScript conventions
 * 2. Proper Date objects instead of ISO strings
 * 3. Silent bugs prevented (e.g., accessing wrong field name)
 */
function transformPayload(record: any): any {
  // First convert to camelCase (handles nested objects too)
  const camelRecord = camelcaseKeys(record, { deep: true });

  // Then parse ISO date strings to Date objects
  const parsed = { ...camelRecord };
  if (parsed.createdAt && typeof parsed.createdAt === 'string') {
    parsed.createdAt = new Date(parsed.createdAt);
  }
  if (parsed.updatedAt && typeof parsed.updatedAt === 'string') {
    parsed.updatedAt = new Date(parsed.updatedAt);
  }
  if (parsed.startedAt && typeof parsed.startedAt === 'string') {
    parsed.startedAt = new Date(parsed.startedAt);
  }
  if (parsed.completedAt && typeof parsed.completedAt === 'string') {
    parsed.completedAt = new Date(parsed.completedAt);
  }
  return parsed;
}

export interface UseRealtimeEventsOptions {
  deviceId?: string;
  apiKey?: string;
  userId?: string;
  accessToken?: string;
  apiUrl?: string;
  handlers?: RealtimeEventHandlers;
  autoConnect?: boolean;
}

export interface UseRealtimeEventsReturn {
  connected: boolean;
  error: Error | null;
  connect: () => void;
  disconnect: () => void;
  lastEvent: RealtimeEvent | null;
}

/**
 * Hook for connecting to QwickForge realtime SSE stream
 *
 * Supports both device-based auth (CLI) and user-based auth (browser).
 *
 * @example Device-based (CLI):
 * ```tsx
 * const { connected, error } = useRealtimeEvents({
 *   deviceId: 'device-123',
 *   apiKey: 'qwf_dev_...',
 *   handlers: {
 *     onPromptInsert: (prompt) => console.log('New prompt:', prompt),
 *     onExecutionUpdate: (execution) => console.log('Execution updated:', execution)
 *   }
 * });
 * ```
 *
 * @example User-based (browser):
 * ```tsx
 * const { connected, error } = useRealtimeEvents({
 *   userId: session.user.id,
 *   accessToken: session.accessToken,
 *   handlers: {
 *     onPromptInsert: (prompt) => console.log('New prompt:', prompt),
 *     onExecutionUpdate: (execution) => console.log('Execution updated:', execution)
 *   }
 * });
 * ```
 */
export function useRealtimeEvents({
  deviceId,
  apiKey,
  userId,
  accessToken,
  apiUrl = 'https://api.qwickforge.com',
  handlers = {},
  autoConnect = true,
}: UseRealtimeEventsOptions): UseRealtimeEventsReturn {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const handlersRef = useRef(handlers);
  const isConnectingRef = useRef(false); // Track if connection is in progress
  const instanceIdRef = useRef(`hook-${Date.now()}-${Math.random().toString(36).substring(7)}`); // Unique ID for debugging

  // Store auth values in refs to keep them stable
  const authRef = useRef({ userId, accessToken, deviceId, apiKey, apiUrl });

  // Update handlers ref when they change
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  // Update auth ref when values change
  useEffect(() => {
    authRef.current = { userId, accessToken, deviceId, apiKey, apiUrl };
  }, [userId, accessToken, deviceId, apiKey, apiUrl]);

  const handleEvent = useCallback((eventType: string, data: string) => {
    try {
      const parsed: RealtimeEvent = JSON.parse(data);
      console.log(`[useRealtimeEvents] ${eventType}:`, parsed);
      setLastEvent(parsed);

      // Extract record from DatabaseEvent structure (payload.new for INSERT/UPDATE)
      const wireRecord = parsed.payload?.new || parsed.payload;

      switch (eventType) {
        case 'connected':
          setConnected(true);
          setError(null);
          handlersRef.current.onConnected?.();
          break;
        case 'heartbeat':
          // Server sends heartbeat every 1 minute to keep connection alive
          console.log('[useRealtimeEvents] ♥ Heartbeat received');
          break;
        case 'prompt_insert':
          if (wireRecord) {
            handlersRef.current.onPromptInsert?.(transformPayload(wireRecord) as DbPrompt);
          }
          break;
        case 'prompt_update':
          if (wireRecord) {
            handlersRef.current.onPromptUpdate?.(transformPayload(wireRecord) as DbPrompt);
          }
          break;
        case 'execution_insert':
          if (wireRecord) {
            handlersRef.current.onExecutionInsert?.(transformPayload(wireRecord) as DbExecution);
          }
          break;
        case 'execution_update':
          if (wireRecord) {
            handlersRef.current.onExecutionUpdate?.(transformPayload(wireRecord) as DbExecution);
          }
          break;
        case 'project_update':
          if (wireRecord) {
            handlersRef.current.onProjectUpdate?.(transformPayload(wireRecord) as DbProject);
          }
          break;
      }
    } catch (err) {
      console.error(`[useRealtimeEvents] Failed to parse ${eventType} event:`, err);
    }
  }, []);

  const connect = useCallback(() => {
    // Prevent duplicate connections
    if (eventSourceRef.current || isConnectingRef.current) {
      console.log('[useRealtimeEvents] Already connected or connecting', {
        hasEventSource: !!eventSourceRef.current,
        isConnecting: isConnectingRef.current,
      });
      return;
    }

    // Get current auth values from ref (not closure)
    const { userId, accessToken, deviceId, apiKey, apiUrl } = authRef.current;

    // Validate: must have either (deviceId + apiKey) or (userId + accessToken)
    if (!userId && (!deviceId || !apiKey)) {
      console.error('[useRealtimeEvents] Must provide either userId or (deviceId + apiKey)');
      return;
    }

    if (userId && !accessToken) {
      console.error('[useRealtimeEvents] accessToken required for user-based authentication');
      return;
    }

    // Mark as connecting
    isConnectingRef.current = true;

    try {
      const url = new URL('/api/realtime/stream', apiUrl);

      if (userId && accessToken) {
        // User-based auth (browser)
        url.searchParams.set('user_id', userId);

        // ALWAYS use native EventSource for reliability (same as forge-service CLI)
        // Pass access_token as query param since EventSource doesn't support custom headers
        url.searchParams.set('access_token', accessToken);

        console.log('[useRealtimeEvents] Using native EventSource (user auth):', url.toString());

        const eventSource = new EventSource(url.toString());
        eventSourceRef.current = eventSource;
        isConnectingRef.current = false;

        // Connection established
        eventSource.onopen = () => {
          console.log('[useRealtimeEvents] ✓ EventSource connection opened');
          (window as any).__sseConnectTime = Date.now();
          setConnected(true);
          setError(null);
          handlersRef.current.onConnected?.();
        };

        // Listen for all event types
        eventSource.addEventListener('connected', (e) => handleEvent('connected', e.data));
        eventSource.addEventListener('heartbeat', (e) => handleEvent('heartbeat', e.data));
        eventSource.addEventListener('prompt_insert', (e) => handleEvent('prompt_insert', e.data));
        eventSource.addEventListener('prompt_update', (e) => handleEvent('prompt_update', e.data));
        eventSource.addEventListener('execution_insert', (e) => handleEvent('execution_insert', e.data));
        eventSource.addEventListener('execution_update', (e) => handleEvent('execution_update', e.data));
        eventSource.addEventListener('project_update', (e) => handleEvent('project_update', e.data));

        // Error handling - IMPORTANT: Do NOT close EventSource, let browser auto-reconnect
        eventSource.onerror = (e) => {
          console.error('[useRealtimeEvents] SSE error (browser will auto-reconnect):', e);
          setConnected(false);
          const err = new Error('SSE connection error');
          setError(err);
          handlersRef.current.onError?.(err);
          // NOTE: Not closing eventSource - browser handles reconnection automatically
        };

      } else {
        // Device-based auth (CLI) - use native EventSource with query params
        url.searchParams.set('device_id', deviceId!);
        url.searchParams.set('api_key', apiKey!);

        console.log('[useRealtimeEvents] Connecting to SSE stream (device auth):', url.toString());

        const eventSource = new EventSource(url.toString());
        eventSourceRef.current = eventSource;
        isConnectingRef.current = false; // Clear connecting flag

        // Connection established
        eventSource.addEventListener('connected', (e) => {
          handleEvent('connected', e.data);
        });

        // Heartbeat events
        eventSource.addEventListener('heartbeat', (e) => {
          handleEvent('heartbeat', e.data);
        });

        // Prompt events
        eventSource.addEventListener('prompt_insert', (e) => {
          handleEvent('prompt_insert', e.data);
        });

        eventSource.addEventListener('prompt_update', (e) => {
          handleEvent('prompt_update', e.data);
        });

        // Execution events
        eventSource.addEventListener('execution_insert', (e) => {
          handleEvent('execution_insert', e.data);
        });

        eventSource.addEventListener('execution_update', (e) => {
          handleEvent('execution_update', e.data);
        });

        // Project events
        eventSource.addEventListener('project_update', (e) => {
          handleEvent('project_update', e.data);
        });

        // Error handling
        eventSource.onerror = (e) => {
          console.error('[useRealtimeEvents] SSE error:', e);
          isConnectingRef.current = false; // Clear connecting flag
          const err = new Error('SSE connection error');
          setError(err);
          setConnected(false);
          handlersRef.current.onError?.(err);

          // Clean up on error
          eventSource.close();
          eventSourceRef.current = null;
        };
      }
    } catch (err) {
      console.error('[useRealtimeEvents] Failed to connect:', err);
      isConnectingRef.current = false; // Clear connecting flag
      const error = err instanceof Error ? err : new Error('Failed to connect to SSE stream');
      setError(error);
      handlersRef.current.onError?.(error);
    }
  }, [handleEvent]); // Only depend on handleEvent, use authRef for auth values

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      console.log('[useRealtimeEvents] Disconnecting from SSE stream');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      isConnectingRef.current = false;
      setConnected(false);
      handlersRef.current.onDisconnected?.();
    }
  }, []);

  // Auto-connect on mount if enabled
  useEffect(() => {
    const instanceId = instanceIdRef.current;
    console.log(`[useRealtimeEvents ${instanceId}] Hook mounted, autoConnect=${autoConnect}`);

    // Only connect if not already connected
    if (autoConnect && (userId || (deviceId && apiKey)) && !eventSourceRef.current) {
      console.log(`[useRealtimeEvents ${instanceId}] Auto-connecting on mount`);
      connect();
    }

    // Cleanup on unmount
    return () => {
      console.log(`[useRealtimeEvents ${instanceId}] Cleanup on unmount`);
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount/unmount

  return {
    connected,
    error,
    connect,
    disconnect,
    lastEvent,
  };
}
